// Tasks > Stats and Alerts panels (docs/TASK-STATS-SPEC.md, owner 2026-11-02).
// The server scopes by clinic (rule 14); this only draws what it returns.
import type { JSX } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { useResource, refetchResource, peekResource } from '../lib/store';
import { session } from '../lib/session';
import { LOCATION_NAMES, orderLocations, type LocationCode } from '../lib/locations';
import { LineChart, type ChartSelection, type ChartSeries } from './charts/LineChart';
import {
  ALERT_TYPE_LABELS, ALERTS_SUMMARY_KEY, TASK_STATS_LIST_KEY, alertsKey, defaultFrom, driveFileUrl,
  fetchAlerts, fetchAlertsSummary, fetchStats, fetchStatsList, reviewAlert, statsKey,
  type AlertType, type AlertsSummary, type CompletionPoint, type ReportPoint, type TaskAlert,
  type TaskAlertsData, type TaskStatsData, type StatsTaskList,
} from '../lib/taskStats';

export function canSeeSourceFiles(): boolean {
  const me = session.value.me;
  return Boolean(me && !me.viewingAs && (me.role === 'OWNER' || me.role === 'DIRECTOR'));
}

/** Re-ask the server once when a panel opens, so a cached copy never goes stale. */
function refreshIfReady<T>(key: string, fetcher: () => Promise<T>): void {
  if (peekResource<T>(key) !== null) void refetchResource(key, fetcher);
}
function useRefreshOnOpen<T>(key: string, fetcher: () => Promise<T>): void {
  useEffect(() => { refreshIfReady(key, fetcher); }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
}

const clinicName = (c: string): string => LOCATION_NAMES[c as LocationCode] ?? c;

const REPORT_SERIES: Record<string, { key: string; label: string }[]> = {
  DB015_SUSPENDED: [
    { key: 'still_suspended', label: 'Still suspended' }, { key: 'new_since_prev', label: 'New' },
    { key: 'resolved_since_prev', label: 'Resolved' }, { key: 'at_risk', label: 'At risk (4+ failed)' },
  ],
  DB015_FROZEN: [
    { key: 'total_frozen', label: 'Total frozen' }, { key: 'newly_frozen', label: 'Newly frozen' },
    { key: 'reactivated', label: 'Reactivated' },
  ],
  MR245: [{ key: 'new_members', label: 'New members per week' }],
};

const isReport = (p: ReportPoint | CompletionPoint): p is ReportPoint => 'values' in p;

function StatsClinic(
  { clinic, data, report }: { clinic: string; data: TaskStatsData; report: string | undefined },
): JSX.Element {
  const [sel, setSel] = useState<ChartSelection | null>(null);
  const points = data.series[clinic] ?? [];
  const source = canSeeSourceFiles();
  let charts: { id: string; labels: string[]; series: ChartSeries[]; yMax?: number }[];
  let detail: JSX.Element | null = null;
  if (data.kind === 'report') {
    const rp = points.filter(isReport);
    const labels = rp.map((p) => p.report_date ?? p.week_start ?? '');
    const defs = REPORT_SERIES[report ?? ''] ?? [];
    charts = [{
      id: 'report', labels,
      series: defs.map((d) => ({ key: d.key, label: d.label, values: rp.map((p) => (p.values[d.key] ?? null)) })),
    }];
    const p = sel ? rp[sel.index] : null;
    if (sel && p) {
      const label = defs.find((d) => d.key === sel.seriesKey)?.label ?? sel.seriesKey;
      detail = (
        <div data-testid="point-detail" class="muted">
          {label}: {p.values[sel.seriesKey]} - report date {p.report_date ?? p.week_start}
          {source && p.drive_file_id ? (
            <> - <a href={driveFileUrl(p.drive_file_id)} target="_blank" rel="noopener noreferrer">source file</a></>
          ) : null}
        </div>
      );
    }
  } else {
    const cp = points.filter((p): p is CompletionPoint => !isReport(p));
    const labels = cp.map((p) => p.period_label);
    charts = [
      {
        id: 'pct', labels, yMax: 100,
        series: [
          { key: 'pct_done', label: '% done', values: cp.map((p) => p.pct_done) },
          { key: 'pct_on_time', label: '% on time', values: cp.map((p) => p.pct_on_time) },
        ],
      },
      {
        id: 'counts', labels,
        series: [
          { key: 'sent_back', label: 'Sent back', values: cp.map((p) => p.sent_back) },
          { key: 'days_late', label: 'Days late', values: cp.map((p) => p.days_late) },
        ],
      },
    ];
    const p = sel ? cp[sel.index] : null;
    if (sel && p) {
      detail = (
        <div data-testid="point-detail" class="muted">
          {p.period_label} ({p.period_start} to {p.period_end}): {p.done} of {p.total} done,
          {' '}{p.pct_done ?? 'n/a'}% done, {p.pct_on_time ?? 'n/a'}% on time, {p.sent_back} sent back, {p.days_late} days late
        </div>
      );
    }
  }
  return (
    <div class="card" data-testid={`stats-clinic-${clinic}`}>
      <h3>{clinicName(clinic)}</h3>
      {points.length === 0 ? <p class="muted">No data yet.</p> : (
        <>
          {charts.map((c) => (
            <LineChart key={c.id} testId={`chart-${clinic}-${c.id}`} labels={c.labels} series={c.series}
              yMax={c.yMax} selected={sel} onSelect={setSel} />
          ))}
          {detail}
        </>
      )}
    </div>
  );
}

export function TaskStatsView(): JSX.Element {
  const list = useResource<StatsTaskList>(TASK_STATS_LIST_KEY, fetchStatsList);
  useRefreshOnOpen(TASK_STATS_LIST_KEY, fetchStatsList);
  const [task, setTask] = useState<string>('');
  const tasks = list.status === 'ready' ? list.data.tasks : [];
  const chosen = task || tasks[0]?.key || '';
  const key = chosen ? statsKey(chosen) : null;
  const stats = useResource<TaskStatsData>(key, () => fetchStats(chosen));
  useEffect(() => { if (key) refreshIfReady(key, () => fetchStats(chosen)); }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  const clinics = orderLocations((stats.status === 'ready' ? stats.data.locations : []) as LocationCode[]);
  const report = tasks.find((t) => t.key === chosen)?.kind === 'report' ? chosen.replace(/^report:/, '') : undefined;
  return (
    <div data-testid="tasks-stats">
      <div class="section-head"><h2>Stats</h2></div>
      {list.status === 'fault' ? <p role="alert">Could not load the task list: {list.error}</p> : null}
      <label>Task{' '}
        <select data-testid="stats-task" value={chosen} onChange={(e) => setTask((e.target as HTMLSelectElement).value)}>
          {tasks.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
        </select>
      </label>
      {stats.status === 'fault' ? <p role="alert">Could not load stats: {stats.error}</p> : null}
      {stats.status === 'loading' && key ? <p class="muted">Loading...</p> : null}
      {stats.status === 'ready' ? clinics.map((c) => (
        <StatsClinic key={c} clinic={c} data={stats.data} report={report} />
      )) : null}
    </div>
  );
}

const TYPE_OPTIONS: AlertType[] = ['WAIVED_PAYMENT', 'POINTS', 'REFUND', 'DISCOUNT'];

function alertAmount(a: TaskAlert): string {
  if (a.points !== null) return `${a.points} points`;
  if (a.amount !== null) return `$${a.amount.toFixed(2)}`;
  return '';
}

export function TaskAlertsView(): JSX.Element {
  const [type, setType] = useState('');
  const [from, setFrom] = useState(defaultFrom());
  const [to, setTo] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const key = alertsKey(type, from, to);
  const fetcher = (): Promise<TaskAlertsData> => fetchAlerts(type, from, to);
  const res = useResource<TaskAlertsData>(key, fetcher);
  useRefreshOnOpen(key, fetcher);
  const canReview = canSeeSourceFiles();

  async function review(a: TaskAlert): Promise<void> {
    setBusy(String(a.id)); setErr(null);
    try {
      await reviewAlert(a.id);
      await refetchResource(key, fetcher);
      await refetchResource(ALERTS_SUMMARY_KEY, fetchAlertsSummary);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally { setBusy(null); }
  }

  const clinics = orderLocations((res.status === 'ready' ? res.data.locations : []) as LocationCode[]);
  return (
    <div data-testid="tasks-alerts">
      <div class="section-head"><h2>Alerts</h2></div>
      <div class="filters" style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
        <label>Type{' '}
          <select data-testid="alerts-type" value={type} onChange={(e) => setType((e.target as HTMLSelectElement).value)}>
            <option value="">All types</option>
            {TYPE_OPTIONS.map((t) => <option key={t} value={t}>{ALERT_TYPE_LABELS[t]}</option>)}
          </select>
        </label>
        <label>From{' '}
          <input data-testid="alerts-from" type="date" value={from} onChange={(e) => setFrom((e.target as HTMLInputElement).value)} />
        </label>
        <label>To{' '}
          <input data-testid="alerts-to" type="date" value={to} onChange={(e) => setTo((e.target as HTMLInputElement).value)} />
        </label>
      </div>
      {err ? <p role="alert">{err}</p> : null}
      {res.status === 'fault' ? <p role="alert">Could not load alerts: {res.error}</p> : null}
      {res.status === 'loading' ? <p class="muted">Loading...</p> : null}
      {res.status === 'ready' && res.data.truncated ? <p class="muted">Showing the newest 1000 alerts; narrow the dates to see older ones.</p> : null}
      {res.status === 'ready' ? clinics.map((c) => {
        const rows = res.data.alerts.filter((a) => a.location_code === c);
        return (
          <div class="card" key={c} data-testid={`alerts-clinic-${c}`}>
            <h3>{clinicName(c)}</h3>
            {rows.length === 0 ? <p class="muted">No alerts in this range.</p> : (
              <table>
                <thead><tr><th>Date</th><th>Type</th><th>Client</th><th>Detail</th><th>Amount</th><th>Employee</th><th>Source</th><th>Review</th></tr></thead>
                <tbody>
                  {rows.map((a) => (
                    <tr key={a.id} data-testid="alert-row" data-reviewed={a.reviewed ? 'true' : 'false'}
                      style={a.reviewed ? { opacity: 0.5 } : undefined}>
                      <td>{a.alert_date}</td>
                      <td>{ALERT_TYPE_LABELS[a.type] ?? a.type}</td>
                      <td>{a.client_name ?? ''}</td>
                      <td>{a.detail ?? ''}</td>
                      <td>{alertAmount(a)}</td>
                      <td>{a.employee ?? ''}</td>
                      <td>
                        {canReview && a.drive_file_id
                          ? <a href={driveFileUrl(a.drive_file_id)} target="_blank" rel="noopener noreferrer">{a.report_date}</a>
                          : a.report_date}
                      </td>
                      <td>
                        {a.reviewed ? (
                          <span data-testid="reviewed-by">Reviewed by {a.reviewed.by} on {a.reviewed.at.slice(0, 10)}</span>
                        ) : canReview ? (
                          <button type="button" data-testid="review-btn" disabled={busy === String(a.id)} onClick={() => { void review(a); }}>
                            Reviewed
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        );
      }) : null}
    </div>
  );
}

/** Unreviewed alert count for the Alerts tab badge. Navigation makes no network
 * call (INV-1): the count is primed by the sign-in load (lib/bootstrap.ts) and
 * only read here; a Reviewed click refetches it, which updates the badge. */
export function useAlertsBadge(): number {
  const primed = peekResource<AlertsSummary>(ALERTS_SUMMARY_KEY) !== null;
  const res = useResource<AlertsSummary>(primed ? ALERTS_SUMMARY_KEY : null, fetchAlertsSummary);
  return res.status === 'ready' ? res.data.unreviewed : 0;
}
